import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { backgroundMusicConfig } from "@/lib/api";

const VIDEO_STATE_EVENT = "nawadah:video-state";

const nativeVideoIsAudible = (video) => (
  video instanceof HTMLVideoElement
  && !video.paused
  && !video.ended
  && !video.muted
  && video.volume > 0
);

export default function BackgroundMusic({ settings }) {
  const audioRef = useRef(null);
  const youtubeVideosRef = useRef(new Set());
  const pausedForVideoRef = useRef(false);
  const interactionUnlockedRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const config = useMemo(() => backgroundMusicConfig(settings), [settings]);
  const currentTrack = config.tracks[activeIndex] || config.tracks[0];

  const hasAudibleVideo = useCallback(() => {
    if (youtubeVideosRef.current.size > 0) return true;
    return Array.from(document.querySelectorAll("video")).some(nativeVideoIsAudible);
  }, []);

  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !config.enabled || !config.autoplay || hasAudibleVideo()) return false;
    try {
      await audio.play();
      interactionUnlockedRef.current = true;
      return true;
    } catch {
      return false;
    }
  }, [config.autoplay, config.enabled, hasAudibleVideo]);

  useEffect(() => {
    if (!config.tracks.length) {
      setActiveIndex(0);
      return;
    }
    setActiveIndex((index) => Math.min(index, config.tracks.length - 1));
  }, [config.tracks.length]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !config.enabled || !currentTrack) return undefined;

    audio.volume = config.volume;
    audio.load();
    void play();
    return () => audio.pause();
  }, [config.enabled, config.volume, currentTrack, play]);

  useEffect(() => {
    if (!config.enabled || !config.autoplay) return undefined;

    const unlock = () => {
      interactionUnlockedRef.current = true;
      if (!hasAudibleVideo()) void play();
    };
    document.addEventListener("pointerdown", unlock, { capture: true, once: true });
    document.addEventListener("keydown", unlock, { capture: true, once: true });
    document.addEventListener("touchstart", unlock, { capture: true, once: true, passive: true });
    return () => {
      document.removeEventListener("pointerdown", unlock, true);
      document.removeEventListener("keydown", unlock, true);
      document.removeEventListener("touchstart", unlock, true);
    };
  }, [config.autoplay, config.enabled, hasAudibleVideo, play]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !config.enabled) return undefined;
    const youtubeVideos = youtubeVideosRef.current;

    const syncPlayback = () => {
      window.setTimeout(() => {
        if (hasAudibleVideo()) {
          if (!audio.paused) {
            pausedForVideoRef.current = true;
            audio.pause();
          }
          return;
        }
        if (pausedForVideoRef.current) {
          pausedForVideoRef.current = false;
          void play();
        }
      }, 0);
    };

    const onYouTubeState = (event) => {
      const id = String(event.detail?.id || "youtube-video");
      if (event.detail?.active) youtubeVideos.add(id);
      else youtubeVideos.delete(id);
      syncPlayback();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && interactionUnlockedRef.current && !hasAudibleVideo()) {
        void play();
      }
    };

    document.addEventListener("play", syncPlayback, true);
    document.addEventListener("pause", syncPlayback, true);
    document.addEventListener("ended", syncPlayback, true);
    document.addEventListener("volumechange", syncPlayback, true);
    window.addEventListener(VIDEO_STATE_EVENT, onYouTubeState);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("play", syncPlayback, true);
      document.removeEventListener("pause", syncPlayback, true);
      document.removeEventListener("ended", syncPlayback, true);
      document.removeEventListener("volumechange", syncPlayback, true);
      window.removeEventListener(VIDEO_STATE_EVENT, onYouTubeState);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      youtubeVideos.clear();
    };
  }, [config.enabled, hasAudibleVideo, play]);

  if (!config.enabled || !currentTrack) return null;

  const playNext = () => {
    if (config.tracks.length > 1) {
      setActiveIndex((index) => {
        const nextIndex = index + 1;
        if (nextIndex < config.tracks.length) return nextIndex;
        return config.loop ? 0 : index;
      });
      return;
    }
    if (config.loop && audioRef.current) {
      audioRef.current.currentTime = 0;
      void play();
    }
  };

  return (
    <audio
      ref={audioRef}
      src={currentTrack.url}
      preload="auto"
      onEnded={playNext}
      className="hidden"
      aria-hidden="true"
      data-testid="background-music-player"
      data-track-title={currentTrack.title}
    />
  );
}
